import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  fetchCurrentAccount,
  login as loginRequest,
  logout as logoutRequest,
  startDemo as startDemoRequest,
} from "../api/auth-api";
import type { ApiClient } from "../api/client";
import { apiClient } from "../api/client";
import { NetworkError, userFacingMessage } from "../api/errors";
import {
  clearLastAccount,
  isDemoExpired,
  loadLastAccount,
  saveLastAccount,
} from "../offline/last-account-store";
import { clearAllCollectionSnapshots } from "../offline/collection-snapshot-store";
import {
  AUTH_BOOTSTRAP_TIMEOUT_MS,
  withAuthBootstrapTimeout,
} from "./bootstrap-timeout";
import { AuthContext } from "./AuthContext";
import type { AuthState } from "./types";

type AuthProviderProps = {
  children: ReactNode;
  client?: ApiClient;
  bootstrapTimeoutMs?: number;
};

export function AuthProvider({
  children,
  client = apiClient,
  bootstrapTimeoutMs = AUTH_BOOTSTRAP_TIMEOUT_MS,
}: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    account: null,
  });
  const bootstrapGenerationRef = useRef(0);

  const markUnauthenticated = useCallback(() => {
    clearLastAccount();
    clearAllCollectionSnapshots();
    setState({ status: "unauthenticated", account: null });
  }, []);

  const applyBootstrapResult = useCallback(
    (generation: number, account: Awaited<ReturnType<typeof fetchCurrentAccount>>) => {
      if (generation !== bootstrapGenerationRef.current) {
        return;
      }
      if (account) {
        if (isDemoExpired(account)) {
          clearLastAccount();
          clearAllCollectionSnapshots();
          setState({ status: "unauthenticated", account: null });
          return;
        }
        saveLastAccount(account);
        setState({ status: "authenticated", account });
        return;
      }
      clearLastAccount();
      clearAllCollectionSnapshots();
      setState({ status: "unauthenticated", account: null });
    },
    [],
  );

  const applyBootstrapFailure = useCallback((generation: number, error: unknown) => {
    if (generation !== bootstrapGenerationRef.current) {
      return;
    }
    if (error instanceof NetworkError) {
      const cached = loadLastAccount();
      if (cached) {
        setState({ status: "authenticated", account: cached });
        return;
      }
    }
    setState({
      status: "error",
      account: null,
      message: userFacingMessage(error),
    });
  }, []);

  const runBootstrap = useCallback(async () => {
    const generation = bootstrapGenerationRef.current + 1;
    bootstrapGenerationRef.current = generation;
    setState({ status: "loading", account: null });

    const controller = new AbortController();
    const pending = fetchCurrentAccount(client, { signal: controller.signal });
    void pending.catch(() => {
      // Timeout abort must not surface as an unhandled rejection.
    });

    try {
      const account = await withAuthBootstrapTimeout(pending, bootstrapTimeoutMs);
      applyBootstrapResult(generation, account);
    } catch (error) {
      // Cancel only an unfinished request. Aborting after success makes Firefox
      // report NS_BINDING_ABORTED and can stall later requests on the same socket.
      controller.abort();
      applyBootstrapFailure(generation, error);
    }
  }, [applyBootstrapFailure, applyBootstrapResult, bootstrapTimeoutMs, client]);

  useEffect(() => {
    const generation = bootstrapGenerationRef.current + 1;
    bootstrapGenerationRef.current = generation;

    client.setAuthExpiryHandler(() => {
      if (generation === bootstrapGenerationRef.current) {
        clearLastAccount();
        clearAllCollectionSnapshots();
        setState({ status: "unauthenticated", account: null });
      }
    });

    const controller = new AbortController();
    const pending = fetchCurrentAccount(client, { signal: controller.signal });
    void pending.catch(() => {
      // Unmount/timeout abort must not surface as an unhandled rejection.
    });

    void (async () => {
      try {
        const account = await withAuthBootstrapTimeout(pending, bootstrapTimeoutMs);
        applyBootstrapResult(generation, account);
      } catch (error) {
        controller.abort();
        applyBootstrapFailure(generation, error);
      }
    })();

    return () => {
      bootstrapGenerationRef.current += 1;
      controller.abort();
      client.setAuthExpiryHandler(null);
    };
  }, [applyBootstrapFailure, applyBootstrapResult, bootstrapTimeoutMs, client]);

  const login = useCallback(
    async (username: string, password: string) => {
      const account = await loginRequest(client, username, password);
      saveLastAccount(account);
      setState({ status: "authenticated", account });
    },
    [client],
  );

  const startDemo = useCallback(async () => {
    const account = await startDemoRequest(client);
    saveLastAccount(account);
    setState({ status: "authenticated", account });
  }, [client]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest(client);
    } finally {
      clearLastAccount();
      clearAllCollectionSnapshots();
      setState({ status: "unauthenticated", account: null });
    }
  }, [client]);

  const value = useMemo(
    () => ({
      ...state,
      login,
      startDemo,
      logout,
      markUnauthenticated,
      retryBootstrap: runBootstrap,
    }),
    [state, login, startDemo, logout, markUnauthenticated, runBootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
